import React, {
  useEffect, useContext, useRef, useState, Suspense
} from 'react';
import proptypes from 'prop-types';
import {
  Skeleton,
  SkeletonBlock,
  SkeletonContent,
  SkeletonLine
} from '@one-thd/sui-atomic-components';
import { QueryContext, useLazyDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { dataModel } from '../dataModel';
import { OrderSampleContent } from './OrderSampleContent';
import { useOrderSample } from '../hooks/useOrderSample';
import { publish } from '../analytics';
import { SAMPLE_AVAILABILITY } from '../constants';

const { bool, node, string } = proptypes;

const SampleDrawer = React.lazy(() => import(
  /* webpackChunkName: "order-sample-drawer" */
  './SampleDrawer/SampleDrawer')
);

const defaultState = {
  sampleDrawerRequested: false,
  gettingSampleData: null,
  sampleAvailability: SAMPLE_AVAILABILITY.AVAILABLE
};

export const OrderSample = ({
  children = 'Need a closer look?',
  disableDrawerCTA = false,
  disableSampleCall = false,
  itemId,
  link = false,
  showTitle = true,
  showIconOnly = false,
  className = ''
}) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('order-sample.ready'); }, []);

  const { dataSource } = useContext(QueryContext);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const isPLP = dataSource === 'searchNav';
  // TODO: Consider creating a context for this rather than prop drilling
  const [orderSampleState, setOrderSampleState] = useState(defaultState);
  const {
    sampleDrawerRequested,
    gettingSampleData,
    sampleAvailability
  } = orderSampleState;

  const { storeZip: zipCode } = useStore();
  const { data, loading: dataLoading } = useOrderSample({ disableSampleCall, itemId });
  const [metadataReq, metadataResponse] = useLazyDataModel('metadata', { fetchPolicy: 'cache-first' });
  const metadataHasBeenCalled = useRef(false);

  const {
    discontinued,
    isSuperSku,
    parentId,
    price,
    sampleId,
    sampleProduct
  } = data;

  // When a product is a super sku, it will almost definitely use the sample drawer
  // The only known exception is paint - which can have one sample for all the super skus which will not use the drawer
  const willLikelyUseDrawer = !disableDrawerCTA && isSuperSku;

  const getSampleAvailability = () => {
    let tempSampleAvailability = SAMPLE_AVAILABILITY.AVAILABLE;
    if (discontinued) {
      tempSampleAvailability = SAMPLE_AVAILABILITY.DISCONTINUED;
    } else if (!(sampleProduct.fulfillment?.fulfillmentOptions?.find((option) => option.fulfillable))) {
      tempSampleAvailability = SAMPLE_AVAILABILITY.OUT_OF_STOCK;
    }
    return tempSampleAvailability;
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  // Update sampleAvailability when data changes
  useEffect(() => {
    if (mounted && !willLikelyUseDrawer && sampleProduct) {
      const tempSampleAvailability = getSampleAvailability();
      if (sampleAvailability !== tempSampleAvailability) {
        setOrderSampleState((prevState) => ({
          ...prevState,
          sampleAvailability: tempSampleAvailability,
        }));
      }
    }
  }, [mounted, sampleProduct, discontinued, willLikelyUseDrawer, sampleAvailability]);

  const clickOrderSample = ({ isManualClick }) => {
    // Should perform actions if order sample is clicked manually or if metadata has been loaded (useEffect)
    if (isManualClick || (!metadataResponse?.loading && metadataHasBeenCalled.current)) {
      // Determines if we will ACTUALLY use the drawer
      let shouldUseDrawer = willLikelyUseDrawer;

      // If there's no metadata response, we won't use the drawer
      if (metadataResponse?.data?.metadata === null) {
        shouldUseDrawer = false;
      } else if (metadataResponse?.data?.metadata?.sampleProductDetail) {
        // If there's a valid metadata response, include the hasMultipleSamples check
        const sampleProductDetail = metadataResponse.data.metadata.sampleProductDetail || [];
        const hasMultipleSamples = sampleProductDetail.length
          && new Set(sampleProductDetail.map((detail) => detail.sampleId)).size > 1;
        shouldUseDrawer = shouldUseDrawer && hasMultipleSamples;
      }

      if (shouldUseDrawer) {
        setOrderSampleState((prevState) => {
          return {
            ...prevState,
            // Only getting sample data if it hasn't been called yet
            gettingSampleData: gettingSampleData == null ? true : gettingSampleData,
            sampleDrawerRequested: true
          };
        });
      } else {
        const tempSampleAvailability = getSampleAvailability();
        if (sampleAvailability !== tempSampleAvailability) {
          setOrderSampleState((prevState) => ({
            ...prevState,
            sampleAvailability: tempSampleAvailability,
          }));
        }

        if (tempSampleAvailability === SAMPLE_AVAILABILITY.AVAILABLE) {
          const cartReqParams = {
            itemId: sampleId,
            quantity: 1,
            fulfillmentMethod: 'ShipToHome',
            fulfillmentLocation: zipCode
          };
  
          const cartOptions = {
            channel,
            host: window.location.origin.includes('localhost') ? 'https://www.homedepot.com' : window.location.origin,
            misship: false,
            paypal: false
          };
  
          window.LIFE_CYCLE_EVENT_BUS.trigger('order-sample.add-to-cart', {
            podAction: 'Add to cart',
            podAnchorSku: itemId,
            sampleId,
            parent: 'plp'
          });
  
          window.LIFE_CYCLE_EVENT_BUS.trigger('cart.add-to-cart', {
            cartReqParams,
            cartOptions
          });
        }
      }

      publish('order-sample.click');
    }
  };

  useEffect(() => {
    clickOrderSample({ isManualClick: false });
  }, [metadataResponse]);

  if (!mounted) return null;

  if (dataLoading) {
    let heightUnit;
    if (link) { // specific to PLP and PEP
      if (isPLP) {
        heightUnit = isMobile ? '7' : '9';
      } else { // PEP
        heightUnit = '12';
      }
    } else { // specific to PIP
      heightUnit = isMobile ? '24' : '12';
    }
    return (
      <div className="sui-grid sui-grid-cols-1 sui-w-full">
        <Skeleton disablePadding orientation="horizontal" grow>
          {!isMobile && (
            <SkeletonContent disablePadding density="loose" initialSize="1/4">
              <SkeletonBlock aspect="wide" height={heightUnit} />
            </SkeletonContent>
          )}
          <SkeletonContent disablePadding density="loose" initialSize="3/4" grow>
            <SkeletonLine variant="single" />
          </SkeletonContent>
        </Skeleton>
      </div>
    );
  }

  if (!sampleProduct || !sampleId || (!price && !discontinued)) {
    return null;
  }

  const checkSamplesAvailable = ({ loading, orderSamples }) => {
    let sampleStatus = SAMPLE_AVAILABILITY.AVAILABLE;
    if (!loading) {
      const samplesAvailable = (orderSamples || []).length;
      const inStock = !!orderSamples?.find((sample) => sample.inStockStatus);
      const notDiscontinued = !!orderSamples?.find((sample) => !sample.discontinued);

      const allSamplesOutOfStock = !!(samplesAvailable && !inStock);
      const allSamplesDiscontinued = !!(samplesAvailable && !notDiscontinued);
      if (allSamplesDiscontinued) {
        sampleStatus = SAMPLE_AVAILABILITY.DISCONTINUED;
      } else if (allSamplesOutOfStock) {
        sampleStatus = SAMPLE_AVAILABILITY.OUT_OF_STOCK;
      } else if (samplesAvailable === 0) {
        sampleStatus = SAMPLE_AVAILABILITY.NO_SAMPLES_AVAILABLE;
      }
    }
    setOrderSampleState((prevState) => {
      return {
        ...prevState,
        gettingSampleData: loading,
        sampleAvailability: sampleStatus
      };
    });
  };

  const handleClick = () => {
    /*
      parentId should never be the same as itemId
      there is bad data in the wild that has this, so we need to check for it
      to prevent bad sample calls that never return data or a !loading state

      We're hoping to find the cause for this in the near future, but it seems to be somewhere in Apollo itself
    */
    if (!metadataHasBeenCalled.current && parentId !== itemId) {
      metadataReq({ variables: { parentId } });
      metadataHasBeenCalled.current = true;
    } else {
      clickOrderSample({ isManualClick: true });
    }
  };

  const sampleDrawerClose = () => {
    setOrderSampleState((prevState) => {
      return {
        ...prevState,
        sampleDrawerRequested: false,
      };
    });
  };
  const shouldDrawerOpen = (!gettingSampleData && sampleAvailability === SAMPLE_AVAILABILITY.AVAILABLE);

  return (
    <div data-component="OrderSample" className={className}>
      <OrderSampleContent
        handleClick={handleClick}
        link={link}
        loading={!!gettingSampleData}
        price={price}
        sampleAvailability={sampleAvailability}
        showTitle={showTitle}
        showIconOnly={showIconOnly}
      >
        {children}
      </OrderSampleContent>
      {sampleDrawerRequested
        && (
          <Suspense fallback={<div />}>
            <SampleDrawer
              anchorSampleId={sampleId}
              onClose={sampleDrawerClose}
              onSampleData={checkSamplesAvailable}
              open={shouldDrawerOpen}
              parentId={parentId}
            />
          </Suspense>
        )}
    </div>
  );
};

OrderSample.displayName = 'OrderSample';

OrderSample.dataModel = dataModel;

OrderSample.propTypes = {
  children: node,
  disableDrawerCTA: bool,
  // This is used to prevent the sample call until the consumer is ready
  // Ex: On PLP, we don't want to make a sample call when the user hovers over a super sku, only when they click
  disableSampleCall: bool,
  itemId: string.isRequired,
  link: bool,
  showTitle: bool,
  showIconOnly: bool,
  className: string
};